import React from "react";

export default function transformColumnsForTanStack(cols = [], sampleData = []) {
    return cols
        .filter(col => !col.hide)
        .map((col) => {
            let cellRenderer;
            let rendererType = "default";

            // ---- Determine formatVariant dynamically ----
            let formatVariant = "string"; // default
            const field = col.field || col.accessorKey;
            const headerOrField = `${(col.headerName || col.header || "").toLowerCase()} ${(field || "").toLowerCase()}`.trim();

            // --- Explicit field-based format rules (matches your Excel logic) ---
            // ---- Infer based on field/header name ----
            if (field === "dos") {
                formatVariant = "integer"; // same as Excel cell.z = "#,##0"
            }
            else if (field === "distributorMargin") {
                formatVariant = "percentage"; // same as Excel cell.z = "0.00%"
            }
            else if (["conversionFactor", "territorySeqNo", "distributorCode"].includes(field)) {
                formatVariant = "numeric"; // Excel cell.z = "0"
            }
            else if (/(validatedfromdate|validatedtodate|validateddate)/i.test(field)) {
                formatVariant = "date";
            }
            else if (/(wwid|emailid|isStrategicProduct)/i.test(field)) {
                formatVariant = "string";
            }
            else if (/(value|amount|price|stdnr|rate|actualnr|totaldiff|items|quantity|qty|stock|purchase|return|adjustment|inword|outword|jnjsale|interdealersale|unitgts|subtotal|inward|outward)/.test(headerOrField)) {
                formatVariant = "decimalnumber";
            }
            else if (/^id$|id$/i.test(headerOrField)) {
                formatVariant = "numeric";
            }
            else if (/(month)/i.test(headerOrField)) {
                formatVariant = "monthdate"; 
            }
            else if (/date$/i.test(headerOrField)) {
                formatVariant = "date";
            }

            // ---- Infer by sample data (optional) ----
            if (sampleData.length > 0 && field && field in sampleData[0]) {
                const sampleValue = sampleData.find(d => d[field] !== null && d[field] !== undefined)?.[field];

                if (typeof sampleValue === "number" && formatVariant === "string" && !/(wwid|dos)/i.test(field)) {
                    formatVariant = "numeric";
                }
                else if (sampleValue instanceof Date) {
                    // Check if time part exists
                    const hasTime = sampleValue.getHours() !== 0 || sampleValue.getMinutes() !== 0 || sampleValue.getSeconds() !== 0 || sampleValue.getMilliseconds() !== 0;
                    formatVariant = hasTime ? "datetime" : "date";
                }
                else if (typeof sampleValue === "string" && !Number.isNaN(Date.parse(sampleValue))) {
                    const parsedDate = new Date(sampleValue);
                    const hasTime = parsedDate.getHours() !== 0 || parsedDate.getMinutes() !== 0 || parsedDate.getSeconds() !== 0 || parsedDate.getMilliseconds() !== 0;

                    // Optional: keep your header-based date hint
                    if (/date|time/i.test(headerOrField)) {
                        formatVariant = hasTime ? "datetime" : "date";
                    }
                }
                else if (typeof sampleValue === "boolean") {
                    formatVariant = "decimalnumber";
                }
            }

            // ---- Define cell renderers ----
            if (typeof col.renderCell === "function") {
                rendererType = "renderCell";
                cellRenderer = (cell) => {
                    try {
                        // Build MUI-like param object
                        const originalRow = cell?.row?.original || cell?.row || {};

                        const muiLikeParams = {
                            ...originalRow,             // dataMode, serverFileName etc.
                            row: originalRow,           // keep compatibility
                            value: cell?.getValue?.() ?? cell?.value
                        };

                        return col.renderCell(muiLikeParams);
                    } catch (err) {
                        console.error("renderCell error:", err);
                        return "";
                    }
                };
            }
            else if (typeof col.valueFormatter === "function") {
                rendererType = "valueFormatter";
                cellRenderer = ({ row, getValue }) => {
                    const value = getValue();
                    return col.valueFormatter({ value, row: row.original });
                };
            }
            else {
                cellRenderer = (cell) => cell.getValue();
            }

            return {
                id: col.field || col.id || crypto.randomUUID(),
                accessorKey: field,
                filterFn: "myFilterFn",
                header: col.headerName || '',
                size: col.width,
                showInGrid: true,
                userSelectable: true,
                cell: cellRenderer,
                meta: {
                    rendererType,
                    formatVariant, //  used later in filter + export logic
                    description: col.description || "",
                },
            };
        });
}
